import React from 'react';
import styled from 'styled-components';
import {
  themeGet,
  space,
  SpaceProps,
  size,
  SizeProps,
  minWidth,
  MinWidthProps,
  height,
  HeightProps,
  alignItems,
  AlignItemsProps,
  color,
  ColorProps
} from 'styled-system';
import { Check } from 'styled-icons/fa-solid/Check';

import { getStyledSystemProps } from 'src/helpers';

export type StyledCheckboxProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  defaultChecked?: boolean;
  checked?: boolean;
  children?: React.ReactNode;
  name?: string;
  color?: string;
} & SpaceProps &
  AlignItemsProps &
  SizeProps &
  ColorProps;

export const StyledCheckbox = React.forwardRef(
  (
    { name, checked, children, size, className, ...rest }: StyledCheckboxProps,
    ref: (node: HTMLElement | null) => void
  ) => {
    const [styledProps, restProps] = getStyledSystemProps(rest);
    return (
      <StyledLabel boxColor={rest.color} ref={ref} htmlFor={name} className={className} {...styledProps}>
        <StyledInput id={name} type="checkbox" checked={checked} {...restProps} />
        <FakeCheckbox size={size} minWidth={size} className="fake-checkbox">
          <Check />
        </FakeCheckbox>
        {children}
      </StyledLabel>
    );
  }
);

export const Checkbox = StyledCheckbox;

StyledCheckbox.defaultProps = {
  color: 'primary'
};

const StyledInput = styled.input`
  appearance: none;
  position: absolute;
  top: 0;
  left: 0;
  outline: none;
  margin: 0;
  opacity: 0;
  pointer-events: none;
`;

export const FakeCheckbox = styled.div<SizeProps & MinWidthProps>`
  margin-right: 5px;
  flex-shrink: 0;
  padding: 2px;
  border: 1px solid ${themeGet('colors.paleGreyTwo')};
  color: transparent;
  cursor: pointer;
  border-radius: 2px;
  ${size};
  ${minWidth}

  > svg {
    display: block;
  }
`;
FakeCheckbox.defaultProps = {
  size: 18
};

export const StyledLabel = styled.label<HeightProps & ColorProps>`
  position: relative;
  display: flex;
  align-items: center;
  font-weight: ${themeGet('fontWeights.normal')};
  line-height: 1;
  ${space};
  ${alignItems};
  ${height};
  ${color};

  ${StyledInput}:hover + ${FakeCheckbox} {
    border-color: ${({ boxColor }) => themeGet('colors.' + boxColor)};
  }

  ${StyledInput}:focus + ${FakeCheckbox} {
    border-color: ${props => themeGet('colors.' + props.color)};
  }

  ${StyledInput}:checked + ${FakeCheckbox} {
    background-color: ${props => themeGet('colors.' + props.color)};
    border-color: ${props => themeGet('colors.' + props.color)};
    color: white;
  }
`;
